import React from "react";
import { Link } from "react-router-dom";
import PublicLayout from "./PublicLayout";
import { ArrowRight } from "lucide-react";

const INDUSTRIES = [
  {
    n: "01",
    title: "Mining & Earthmoving",
    body: "Haul trucks, excavators, drills, LHDs, dozers — abrasion-resistant hoses and heavy-duty fittings built to survive the pit. R12/R13/R15 assemblies with spiral guards for Komatsu, CAT, BEML and Tata Hitachi fleets.",
    img: "https://images.pexels.com/photos/34150285/pexels-photo-34150285.jpeg?auto=compress&cs=tinysrgb&w=1600",
    tags: ["R12/R13/R15", "Spiral-guarded", "OEM parts"],
  },
  {
    n: "02",
    title: "Steel & Metals",
    body: "Hi-temp hose assemblies for continuous-casting lines, walking beams, ladle handling. PTFE and fire-sleeved hoses that survive molten-metal proximity. Full traceability for QC audits.",
    img: "https://images.unsplash.com/photo-1496247749665-49cf5b1022e9?auto=format&fit=crop&w=1600&q=80",
    tags: ["PTFE", "Fire-sleeved", "ISO 9001"],
  },
  {
    n: "03",
    title: "Cement & Aggregates",
    body: "Kilns, crushers, conveyor tensioners, packers. Long-length assemblies with weather-resistant covers for outdoor plant use. Bulk-supply contracts with 48-hour dispatch.",
    img: "https://images.pexels.com/photos/38520033/pexels-photo-38520033.jpeg?auto=compress&cs=tinysrgb&w=1600",
    tags: ["Weather-cover", "Bulk supply", "48hr dispatch"],
  },
  {
    n: "04",
    title: "Ports & Marine",
    body: "Reach stackers, RTG cranes, tugs and dredgers. Salt-air resistant fittings (zinc-nickel & stainless), marine-grade PTFE for chemical resistance.",
    img: "https://images.pexels.com/photos/29506757/pexels-photo-29506757.jpeg?auto=compress&cs=tinysrgb&w=1600",
    tags: ["Marine-grade", "Zinc-Nickel", "Stainless"],
  },
  {
    n: "05",
    title: "Construction & Infrastructure",
    body: "Backhoes, motor graders, batching plants and pile drivers. Complete replacement kits stocked for JCB, Escorts, ACE and Mahindra machines.",
    img: "https://images.pexels.com/photos/30094678/pexels-photo-30094678.jpeg?auto=compress&cs=tinysrgb&w=1600",
    tags: ["Replacement kits", "JCB/ACE/Escorts", "In stock"],
  },
  {
    n: "06",
    title: "Power & Energy",
    body: "Thermal plants, hydro, wind and solar tracker assemblies. Long-life hoses that meet plant maintenance-window schedules. Batch traceability for regulatory audits.",
    img: "https://images.pexels.com/photos/10733800/pexels-photo-10733800.jpeg?auto=compress&cs=tinysrgb&w=1600",
    tags: ["Long-life", "Traceable", "Audit-ready"],
  },
];

export default function Industries() {
  return (
    <PublicLayout>
      {/* Hero */}
      <section className="border-b border-white/10" data-testid="industries-hero">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 md:py-32">
          <div className="font-tech text-[10px] uppercase tracking-[0.3em] text-[#E11D2A] mb-6">— Industries Served</div>
          <h1 className="font-industrial uppercase tracking-tighter text-6xl md:text-8xl lg:text-9xl leading-[0.85] text-white max-w-5xl" data-testid="industries-h1">
            Where our<br /> hoses <span className="text-[#E11D2A]">work.</span>
          </h1>
          <p className="mt-8 text-white/70 text-lg max-w-2xl">
            Six industries. Thousands of assemblies. One promise — every hose built for the exact application it's going into.
          </p>
        </div>
      </section>

      {/* Alternating blocks */}
      <section data-testid="industries-list">
        {INDUSTRIES.map((ind, i) => {
          const reverse = i % 2 === 1;
          return (
            <div
              key={ind.title}
              className={`grid lg:grid-cols-12 border-b border-white/10 ${reverse ? "lg:direction-rtl" : ""}`}
              data-testid={`industry-block-${i}`}
            >
              <div className={`lg:col-span-6 relative aspect-[4/3] lg:aspect-auto lg:min-h-[560px] overflow-hidden ${reverse ? "lg:order-2" : ""}`}>
                <img src={ind.img} alt={ind.title} className="w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/20 to-transparent" />
                <div className="absolute top-6 left-6 font-tech text-[10px] uppercase tracking-[0.3em] text-white bg-black/60 backdrop-blur px-3 py-1.5 border border-white/20">
                  {ind.n} / {String(INDUSTRIES.length).padStart(2, "0")}
                </div>
              </div>
              <div className={`lg:col-span-6 p-8 md:p-16 lg:p-20 flex flex-col justify-center ${reverse ? "lg:order-1" : ""}`}>
                <div className="font-tech text-[10px] uppercase tracking-[0.3em] text-[#E11D2A] mb-6">Vertical {ind.n}</div>
                <h2 className="font-industrial uppercase tracking-tight text-4xl md:text-5xl lg:text-6xl leading-[0.9] text-white mb-8">
                  {ind.title}
                </h2>
                <p className="text-white/70 text-lg leading-relaxed max-w-xl">{ind.body}</p>
                <div className="mt-8 flex flex-wrap gap-2">
                  {ind.tags.map((t) => (
                    <span key={t} className="px-3 py-1.5 border border-white/15 font-tech text-[10px] uppercase tracking-[0.2em] text-white/70">{t}</span>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </section>

      {/* CTA */}
      <section className="py-24" data-testid="industries-cta">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <h2 className="font-industrial uppercase tracking-tight text-4xl md:text-5xl text-white leading-none">
            Your industry not listed?<br /><span className="text-[#E11D2A]">We custom-build.</span>
          </h2>
          <Link to="/contact" className="inline-flex items-center gap-3 px-7 py-4 bg-[#E11D2A] hover:bg-[#C81622] transition-colors font-tech text-xs uppercase tracking-[0.25em]" data-testid="industries-cta-link">
            Talk to Sales <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </PublicLayout>
  );
}
